// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { getAPIConfig } from '@tcp/core/src/utils';
import {
  Container,
  ChartHeader,
  Table,
  TableHeaders,
  TableRows,
  TableCells,
} from '../styles/SizeTable.style';

const getKeyBasedOnLocale = (key) => {
  const { locale } = getAPIConfig();
  const lowerLocale = locale?.toLowerCase();
  if (lowerLocale === 'es_us') {
    return {
      NAME: 'NOMBRE',
      SIZE: 'TALLA',
    }[key];
  }
  if (lowerLocale === 'fr_ca') {
    return {
      NAME: 'NOM',
      SIZE: 'TAILLE',
    }[key];
  }

  return {
    NAME: 'NAME',
    SIZE: 'SIZE',
  }[key];
};

export const splitSizeValue = (sizeValue) => {
  const [leftSize, rightSize] = sizeValue.split('|');
  return { leftSize, rightSize };
};

export const groupByName = (sizeVal) => {
  const grouped = sizeVal.reduce((acc, item) => {
    const localeName = getKeyBasedOnLocale('NAME');
    if (!acc[item[localeName]]) {
      acc[item[localeName]] = [];
    }
    acc[item[localeName]].push(item);
    return acc;
  }, {});

  return Object.values(grouped);
};

export const getHeaders = (sizeVal) => {
  if (sizeVal.length === 0) return [];
  return Object.keys(sizeVal[0]).filter((key) => key !== getKeyBasedOnLocale('NAME'));
};

export const extractUniqueLeftSizes = (sizeData) => {
  const groupedRows = {};

  sizeData.forEach((item) => {
    const localeSize = getKeyBasedOnLocale('SIZE');
    const { leftSize } = splitSizeValue(item[localeSize]);
    if (!groupedRows[leftSize]) {
      groupedRows[leftSize] = [];
    }
    groupedRows[leftSize].push(item);
  });

  return groupedRows;
};

const renderSplitRowGroupedTable = (chartHeader, sizeVal, getFilteredValue) => {
  const headers = Object.keys(sizeVal[0]).filter((key) => key !== getKeyBasedOnLocale('SIZE'));
  const groupedSizes = extractUniqueLeftSizes(sizeVal);
  return (
    <div className="dynamic-size-chart-table-section">
      {!isEmpty(chartHeader) ? (
        <ChartHeader className="table-title-section">{chartHeader}</ChartHeader>
      ) : (
        <></>
      )}
      <Container className="table-section">
        <Table>
          <thead>
            <tr>
              {Object.keys(sizeVal[0]).map((key) => (
                <TableHeaders key={key} colSpan={key === getKeyBasedOnLocale('SIZE') ? 2 : 1}>
                  {key}
                </TableHeaders>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedSizes).map((leftSize, groupIndex) =>
              groupedSizes[leftSize].map((item, index) => (
                <TableRows key={index} isOdd={groupIndex % 2 !== 0}>
                  {index === 0 ? (
                    <TableCells rowSpan={groupedSizes[leftSize].length}>{leftSize}</TableCells>
                  ) : null}
                  <TableCells>
                    {splitSizeValue(item[getKeyBasedOnLocale('SIZE')]).rightSize}
                  </TableCells>
                  {headers.map((header) => (
                    <TableCells key={header}>{getFilteredValue(header, item[header])}</TableCells>
                  ))}
                </TableRows>
              ))
            )}
          </tbody>
        </Table>
      </Container>
    </div>
  );
};

const renderSizeColumnSplitViewTable = (
  sizeVal,
  getFilteredValue,
  isSizeColumnSplit,
  chartHeader
) => (
  <div className="dynamic-size-chart-table-section">
    {!isEmpty(chartHeader) ? (
      <ChartHeader className="table-title-section">{chartHeader}</ChartHeader>
    ) : (
      <></>
    )}
    <Container className="table-section">
      <Table>
        <thead>
          <tr>
            {Object.keys(sizeVal[0]).map((key) => (
              <TableHeaders
                key={key}
                colSpan={key === getKeyBasedOnLocale('SIZE') && isSizeColumnSplit ? 2 : 1}
              >
                {key}
              </TableHeaders>
            ))}
          </tr>
        </thead>
        <tbody>
          {sizeVal.map((item, index) => (
            <TableRows
              key={index}
              id={`table_${index % 2 !== 0 ? 'odd_row' : 'even_row'}_${index}`}
              isOdd={index % 2 !== 0}
            >
              {Object.entries(item).map(([key, value], idx) => (
                <React.Fragment key={idx}>
                  {key === getKeyBasedOnLocale('SIZE') && isSizeColumnSplit ? (
                    <>
                      <TableCells isSingleSizeColumn={Object.keys(sizeVal[0]).length === 1}>
                        {splitSizeValue(value).leftSize}
                      </TableCells>
                      <TableCells isSingleSizeColumn={Object.keys(sizeVal[0]).length === 1}>
                        {splitSizeValue(value).rightSize}
                      </TableCells>
                    </>
                  ) : (
                    <TableCells>{getFilteredValue(key, value)}</TableCells>
                  )}
                </React.Fragment>
              ))}
            </TableRows>
          ))}
        </tbody>
      </Table>
    </Container>
  </div>
);

const TableRowGrouped = ({ group, headers, getFilteredValue, groupIndex }) => (
  <>
    <TableRows isOdd={groupIndex % 2 !== 0}>
      <TableCells rowSpan={group.length}>{group[0][getKeyBasedOnLocale('NAME')]}</TableCells>
      {headers.map((header, index) => (
        <TableCells key={index}>{getFilteredValue(header, group[0][header])}</TableCells>
      ))}
    </TableRows>
    {group.slice(1).map((item, index) => (
      <TableRows key={index} isOdd={groupIndex % 2 !== 0}>
        {headers.map((header, idx) => (
          <TableCells key={idx}>{getFilteredValue(header, item[header])}</TableCells>
        ))}
      </TableRows>
    ))}
  </>
);

const renderRowGroupedTable = (chartHeader, sizeVal, getFilteredValue) => (
  <div className="dynamic-size-chart-table-section">
    {!isEmpty(chartHeader) ? (
      <ChartHeader className="table-title-section">{chartHeader}</ChartHeader>
    ) : (
      <></>
    )}
    <Container className="table-section">
      <Table>
        <thead>
          <tr>
            {Object.keys(sizeVal[0]).map((key, index) => (
              <TableHeaders
                key={`table_header_${key.toLowerCase()}_${index}`}
                id={`table_header_${key.toLowerCase()}_${index}`}
              >
                {key !== getKeyBasedOnLocale('NAME') ? key : ''}
              </TableHeaders>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupByName(sizeVal).map((group, index) => (
            <React.Fragment key={index}>
              <TableRowGrouped
                group={group}
                headers={getHeaders(sizeVal)}
                getFilteredValue={getFilteredValue}
                groupIndex={index}
              />
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </Container>
  </div>
);

const renderDefaultViewTable = (sizeVal, getFilteredValue) => (
  <Table>
    <thead>
      <tr>
        {Object.keys(sizeVal[0]).map((key, index) => (
          <TableHeaders
            key={`table_header_${key.toLowerCase()}_${index}`}
            id={`table_header_${key.toLowerCase()}_${index}`}
          >
            {key}
          </TableHeaders>
        ))}
      </tr>
    </thead>
    <tbody>
      {sizeVal.map((size, index) => (
        <TableRows
          key={`table_row_${index}`}
          id={`table_${index % 2 !== 0 ? 'odd_row' : 'even_row'}_${index}`}
          isOdd={index % 2 !== 0}
        >
          {Object.entries(size).map(([key, value], idx) => (
            <TableCells
              key={`table_cell_${key.toLowerCase()}_${idx}`}
              id={`table_row_${key.toLowerCase()}_${idx}`}
            >
              {getFilteredValue(key, value)}
            </TableCells>
          ))}
        </TableRows>
      ))}
    </tbody>
  </Table>
);

const SizeTable = ({ sizeData, getFilteredValue }) => {
  const {
    chartHeader = '',
    size: sizeVal = [],
    isSizeColumnSplit = false,
    isTwoRows = false,
  } = !isEmpty(sizeData) && sizeData;
  const isRowGrouped = isSizeColumnSplit && isTwoRows;

  if (isEmpty(sizeVal)) return null;

  if (isSizeColumnSplit && !isRowGrouped) {
    return renderSizeColumnSplitViewTable(
      sizeVal,
      getFilteredValue,
      isSizeColumnSplit,
      chartHeader
    );
  }

  if (isTwoRows && !isRowGrouped) {
    return renderRowGroupedTable(chartHeader, sizeVal, getFilteredValue);
  }

  if (isRowGrouped) {
    return renderSplitRowGroupedTable(chartHeader, sizeVal, getFilteredValue);
  }

  return (
    <div className="dynamic-size-chart-table-section">
      {!isEmpty(chartHeader) ? (
        <ChartHeader className="table-title-section">{chartHeader}</ChartHeader>
      ) : (
        <></>
      )}
      <Container className="table-section">
        {renderDefaultViewTable(sizeVal, getFilteredValue)}
      </Container>
    </div>
  );
};

export default SizeTable;
